import { MapPin } from 'lucide-react'

import type { LatLng } from '@/components/maps/map-controllers'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

export type PlaceSearchOption =
    | { kind: 'coords'; position: LatLng }
    | {
          kind: 'place'
          placeId: string
          mainText: string
          secondaryText: string
      }

/**
 * Danh sách gợi ý dưới ô tìm vị trí. Không dùng Popover vì Popover portal ra
 * `body` — bản đồ toàn màn hình sẽ che mất danh sách.
 */
export function PlaceSearchOptions({
    id,
    options,
    active,
    message,
    onActive,
    onChoose,
}: {
    id: string
    options: PlaceSearchOption[]
    active: number
    /** Lý do không có gợi ý (không tìm thấy / lỗi); chỉ hiện khi danh sách rỗng. */
    message: string | null
    onActive: (index: number) => void
    onChoose: (option: PlaceSearchOption) => void
}) {
    if (options.length === 0 && !message) return null
    return (
        <ul
            id={id}
            role="listbox"
            aria-label={m.map_search_label()}
            className={twJoin(
                'absolute inset-x-0 top-full mt-1 max-h-72 overflow-y-auto',
                'rounded-lg bg-background py-1 text-sm text-foreground shadow-lg',
            )}
        >
            {options.map((o, i) => (
                <li
                    key={o.kind === 'coords' ? 'coords' : o.placeId}
                    role="option"
                    aria-selected={i === active}
                    // mousedown + preventDefault: chọn trước khi ô nhập mất
                    // focus (blur đóng danh sách).
                    onMouseDown={(e) => {
                        e.preventDefault()
                        onChoose(o)
                    }}
                    onMouseEnter={() => onActive(i)}
                    className={twJoin(
                        'flex cursor-pointer items-start gap-2 px-3 py-2',
                        i === active && 'bg-accent',
                    )}
                >
                    <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <OptionText option={o} />
                </li>
            ))}
            {options.length === 0 && message && (
                <li className="px-3 py-2 text-muted-foreground">{message}</li>
            )}
        </ul>
    )
}

function OptionText({ option }: { option: PlaceSearchOption }) {
    if (option.kind === 'coords') {
        return (
            <span className="min-w-0 tabular-nums">
                {m.map_search_coords({
                    lat: String(option.position.lat),
                    lng: String(option.position.lng),
                })}
            </span>
        )
    }
    return (
        <span className="min-w-0">
            <span className="block truncate font-medium">
                {option.mainText}
            </span>
            {option.secondaryText && (
                <span className="block truncate text-xs text-muted-foreground">
                    {option.secondaryText}
                </span>
            )}
        </span>
    )
}
